<template>
  <div
    v-if="data"
    class="articles-view"
  >
    <page-header
      :header="articleHeader(data)"
    >
      <template
        v-if="author"
        slot="subtitle"
      >
        <author-header
          :author="author"
          :link="{ path: 'analysts', id: 'analystId', type: 'channel' }"
        />
      </template>

      <button
          v-if="canEdit && showRepublish"
          class="btn btn-primary mr-2"
          @click="$router.push({ name: 'articles.republish', params: { articleId: data.id } })"
      >
        Republish
      </button>

      <button
        v-if="canEdit"
        class="btn btn-primary mr-2"
        @click="$router.push({ name: 'articles.edit', params: { id: data.id } })"
      >
        Edit
      </button>
      <dropdown
        trigger="click"
        class="no-chev"
        alignment="right"
      >
        <template v-slot:trigger>
          <button class="btn btn-outline-primary btn--more">
            <icon icon="more-vertical" />
          </button>
        </template>
        <dropdown-option
          v-if="$login.can('articles.update') && data.pdf"
          class="page-header-dropdown-item"
          @click.native="downloadPdf"
        >
          <icon
            class="mr-1"
            icon="save"
          />
          Download Raw PDF
        </dropdown-option>
        <dropdown-option
          class="page-header-dropdown-item"
          @click.native="previewEmail"
        >
          <icon
            class="mr-1"
            icon="email"
          />
          Preview Email
        </dropdown-option>
        <dropdown-option
          @click.native="viewOnPortal"
          class="page-header-dropdown-item"
        >
          <icon
            icon="preview"
            class="mr-1"
          />
          View On Insights
        </dropdown-option>
      </dropdown>
    </page-header>

    <tabs>
      <!-- Analytics tab -->
      <tab
        name="Analytics"
        v-if="$login.canAnalytics()"
      >
        <!-- :data="{...data, ...{id: 2705}}" -->
        <analytics
          :data="data"
        />
      </tab>

      <!-- Activity tab -->
      <tab
        name="Activity"
        v-if="$login.can('articles.audit')"
      >
        <activity
          :data="data"
          type="article"
        />
      </tab>

      <!-- Details tab -->
      <tab name="Details">
        <read-only
          :data="data"
          :alerts="alerts"
        />
      </tab>
      <!-- Source tab -->
      <tab v-if="data.source" name="Source">
        <the-source
          :data="data"
        />
      </tab>

      <tab
        name="Alerts"
        v-if="$login.can('alerts.view')"
      >
        <alerts :object="data" />
      </tab>
    </tabs>
    <html-preview ref="htmlPreview" />
  </div>
</template>

<script>
import axios from '@/axios'
import HtmlPreview from '@/components/HtmlPreview'
import Tab from '@/components/Tab'
import Tabs from '@/components/Tabs'
import Analytics from './Analytics'
import Activity from '@/components/Activity'
import {popup} from '@/helpers'
import ReadOnly from './ReadOnly'
import TheSource from './Source'
import Alerts from '@/components/Alerts'
import {successNotification} from '@/notifications'
import mixin from './Mixin'
import Dropdown from '@/components/Dropdown/Dropdown'
import DropdownOption from '@/components/Dropdown/DropdownOption'

export default {
  mixins: [mixin],
  components: {
    HtmlPreview,
    Tabs,
    Tab,
    Analytics,
    Dropdown,
    DropdownOption,
    Activity,
    Alerts,
    ReadOnly,
    TheSource,
  },

  metaInfo () {
    return {
      title: this.data ? this.data.title : 'Article',
    }
  },

  data () {
    return {
      data: null,
      loading: true,
      error: null,
      canPublish: false,
      alertStats: {},
      alerts: [],
      alertid: null,
      recipients: null,
      alertId: null,
      entities: null,
    }
  },

  computed: {
    icon () {
      return this.loading ? 'spinner' : 'plus-circle'
    },

    pdfLink () {
      return this.data.pdf_url + '?token=' + this.$login.getToken()
    },

    showRepublish() {
      return this.$login.isEditor()
    },

    canEdit () {
      return this.$login.can('articles.update')
    },

    author () {
      if (this.data) {
        return this.data.analysts[0]
      }
      return null
    },
  },

  created () {
    this.getData()
  },

  mounted () {
    this.$echo
        .channel('private-admin')
        .listen('ArticlePublishedEvent', this.onArticlePublish)
  },

  methods: {
    async getData () {
      const {articleId} = this.$route.params
      const canGetAlerts = this.$login.can('alerts.view')

      const getArticles = () => axios.get(`/articles/${articleId}`)
      const getAlerts = () => axios.get(`/articles/${articleId}/alerts`)

      this.loading = true
      try {
        const [{data: articleResponse}, {data: alertsResponse}] = await Promise.all([
          getArticles(),
          canGetAlerts ? getAlerts() : {data: null},
        ])

        if (alertsResponse && alertsResponse.data) {
          this.alerts = alertsResponse.data
        }

        this.data = articleResponse.data
        this.loading = false
      } catch (error) {
        this.$router.push('/articles')
      }
    },

    downloadPdf () {
      this.$http.get(`/articles/${this.data.id}/admin-download`).then((r) => {
        popup(this, r.data.data.link)
      })
    },

    onArticlePublish ({article, alertId}) {
      if (article.id === this.data.id) {
        this.$notify(successNotification('Publish complete!'))
        this.data.published_at = article.published_at
        this.data.pdf = article.pdf
        this.data.pdf_url = article.pdf_url
        this.$nextTick(() => {
          this.$router.push({
            name: 'alerts.view',
            params: {
              alertId: alertId,
            },
          })
        })
      }
    },

    viewOnPortal () {
      popup(this, `${this.$portalUrl}article/${this.data.slug}`)
    },

    saveSuccess (response) {
      this.loading = false
      this.$notify(successNotification(`Saved article successfully`))
      this.$router.push({name: 'articles.view', params: {articleId: this.data.id}})
    },

    previewEmail () {
      this.$refs.htmlPreview.preview(this.data.id ? 'article' : 'article-draft', this.data.id ? this.data.id : this.data.article_draft_id)
    },
  },
}
</script>

<style lang="scss">
</style>
